/** The in-shell route table. Every tab renders these inside its own router. */
import React from 'react';
import { matchRoutes, Navigate, useLocation } from 'react-router-dom';
import SearchPage from '../pages/SearchPage';
import DrugDetailPage from '../pages/DrugDetailPage';
import GenericPage from '../pages/GenericPage';
import CompareKeywordPage from '../pages/CompareKeywordPage';
import ManageDraftsPage from '../pages/ManageDraftsPage';
import ResearchResultPage from '../pages/ResearchResultPage';
import WorkstreamPage from '../pages/WorkstreamPage';
import WorkstreamReportPage from '../pages/WorkstreamReportPage';
import AgentEvidencePage from '../pages/AgentEvidencePage';
import SBAViewerPage from '../pages/SBAViewerPage';
import FdaLetterDetailPage from '../pages/FdaLetterDetailPage';
import AccountPage from '../pages/AccountPage';
import RecentSearchesPage from '../pages/RecentSearchesPage';
import StarredListsPage from '../pages/StarredListsPage';
import TrainingPage from '../pages/TrainingPage';
import Home from './Home';
import CohortPage from './CohortPage';
import { isHomePath } from './tabState';

/** D7: `/` with an empty query is Home; `/` with any query is SearchPage. */
const RootRoute: React.FC = () => {
  const { pathname, search } = useLocation();
  return isHomePath(pathname + search) ? <Home /> : <SearchPage />;
};

export const SHELL_ROUTES: { path: string; element: React.ReactElement }[] = [
  { path: '/', element: <RootRoute /> },
  { path: '/drug/:id', element: <DrugDetailPage /> },
  { path: '/generic/:id', element: <GenericPage /> },
  { path: '/compare-keyword', element: <CompareKeywordPage /> },
  { path: '/manage-drafts', element: <ManageDraftsPage /> },
  { path: '/research/:jobId', element: <ResearchResultPage /> },
  { path: '/cohort/:jobId', element: <CohortPage /> },
  { path: '/workstream/:id', element: <WorkstreamPage /> },
  { path: '/workstream/:id/report', element: <WorkstreamReportPage /> },
  { path: '/agents/evidence/*', element: <AgentEvidencePage /> },
  { path: '/sba/documents/:docId/viewer', element: <SBAViewerPage /> },
  { path: '/fda-letter/:letterId', element: <FdaLetterDetailPage /> },
  { path: '/account', element: <AccountPage /> },
  { path: '/subscriptions', element: <Navigate to="/account" replace /> },
  { path: '/recent-searches', element: <RecentSearchesPage /> },
  { path: '/starred-lists', element: <StarredListsPage /> },
  { path: '/training', element: <TrainingPage /> },
];

/**
 * True when the shell hosts this in-app path (query string ignored).
 *
 * Only a same-origin path with ONE leading `/` qualifies: `//host/…`, `/\host`
 * and absolute URLs resolve to another origin (or are one), and a tab holding
 * one would make the outer router write a cross-origin URL.
 */
export function isShellPath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false;
  let u: URL;
  try {
    u = new URL(path, window.location.origin);
  } catch {
    return false;
  }
  if (u.origin !== window.location.origin) return false;
  // Dot segments can resolve a single-slash path to `//…` (`/drug/..//`),
  // which a router write then reads as protocol-relative and throws on.
  if (u.pathname.startsWith('//')) return false;
  return matchRoutes(SHELL_ROUTES, u.pathname) !== null;
}

/**
 * The one spelling of an in-app path (pathname + search) the shell stores and
 * compares. The browser percent-encodes some characters `encodeURIComponent`
 * leaves alone (`'` -> `%27`), so the address bar read back after a write can
 * differ from the path written; every path entering the shell goes through here.
 */
export function normalizeShellPath(path: string): string {
  try {
    const u = new URL(path, window.location.origin);
    return u.pathname + u.search;
  } catch {
    return path;
  }
}
